// Real Shopify store: cart state lives on Shopify's server (the `cart`
// Liquid object), not localStorage — the static site's whole
// getCart/saveCart/addToCart/etc. localStorage system is gone. The header's
// cart count is rendered server-side via {{ cart.item_count }}
// (jyro-header.liquid) and needs no JS to stay correct on page load.
// Add-to-cart / update-quantity on the product and cart pages use Dawn's
// own existing cart AJAX plumbing (assets/cart.js, product-form.js,
// cart-notification.js — already loaded via theme.liquid) rather than
// reinventing it here.

function toast(message) {
  let el = document.getElementById("jsc-toast");
  if (!el) {
    el = document.createElement("div");
    el.id = "jsc-toast";
    el.className =
      "fixed bottom-6 left-1/2 -translate-x-1/2 bg-ink text-cream px-5 py-3 rounded-full text-sm font-medium shadow-lift z-[100] opacity-0 pointer-events-none transition-opacity duration-300";
    document.body.appendChild(el);
  }
  el.textContent = message;
  el.style.opacity = "1";
  clearTimeout(el._t);
  el._t = setTimeout(() => {
    el.style.opacity = "0";
  }, 1800);
}

function initMobileNav() {
  const btn = document.getElementById("nav-toggle");
  const menu = document.getElementById("mobile-menu");
  if (!btn || !menu) return;
  btn.addEventListener("click", () => {
    const isOpen = !menu.hidden;
    menu.hidden = isOpen;
    btn.setAttribute("aria-expanded", String(!isOpen));
  });
}

function initNewsletterForms() {
  document.querySelectorAll("[data-newsletter-form]").forEach((form) => {
    form.addEventListener("submit", (e) => {
      e.preventDefault();
      toast("Thanks — you're on the list.");
      form.reset();
    });
  });
}

function productCardHTML(product) {
  const badge = product.badge
    ? `<span class="absolute top-3 left-3 text-[11px] font-semibold uppercase tracking-wide px-2.5 py-1 ${
        product.badge === "New" ? "badge-new" : "badge-sale"
      }">${product.badge}</span>`
    : "";
  const wasPrice = product.compareAt
    ? `<span class="text-stone line-through ml-2">${formatAUD(
        product.compareAt
      )}</span>`
    : "";
  return `
    <a href="product.html?id=${product.id}" class="product-card group block">
      <div class="relative overflow-hidden bg-off">
        ${badge}
        <img src="${product.image}" alt="${product.name}" class="w-full aspect-[4/5] object-cover" loading="lazy" />
      </div>
      <div class="bg-cream px-3 py-3">
        <p class="text-[10px] uppercase tracking-wider text-stone font-semibold">${product.brand}</p>
        <p class="text-sm font-semibold leading-snug mt-0.5 truncate">${product.name}</p>
        <p class="text-sm mt-0.5">${formatAUD(product.price)}${wasPrice}</p>
      </div>
    </a>
  `;
}

function renderContentBlocks(blocks) {
  return blocks
    .map((block) => {
      if (block.t === "p") {
        return `<p class="text-stone leading-relaxed mb-6">${block.x}</p>`;
      }
      if (block.t === "h") {
        return `<h3 class="font-display text-lg uppercase tracking-nav mt-10 mb-4">${block.x}</h3>`;
      }
      if (block.t === "ul") {
        const items = block.items
          .map(
            (item) =>
              `<li class="flex gap-3 text-stone leading-relaxed"><span class="text-crimson font-bold flex-shrink-0">&mdash;</span><span>${item}</span></li>`
          )
          .join("");
        return `<ul class="space-y-3 mb-6">${items}</ul>`;
      }
      if (block.t === "img") {
        return `<img src="${block.src}" alt="${block.alt || ""}" class="w-full object-cover mb-8" loading="lazy" />`;
      }
      return "";
    })
    .join("");
}

// Splits a product's `content` blocks into a text column (p/h/ul) and an
// image rail (t:"img") that runs alongside it — the "images beside the
// description" layout on the PDP. Each image gets a data-anim variant so
// initDetailImageReveal() can animate it differently from its neighbours.
function renderDetailSections(content) {
  if (!content || !content.length) return "";
  const textBlocks = content.filter((b) => b.t !== "img");
  const imgBlocks = content.filter((b) => b.t === "img");
  const textHTML = renderContentBlocks(textBlocks);
  const variants = ["slide-left", "slide-right", "clip-reveal"];
  const imagesHTML = imgBlocks
    .map((b, i) => {
      const variant = variants[i % variants.length];
      return `
        <figure class="detail-figure" data-detail-img data-anim="${variant}">
          <img src="${b.src}" alt="${b.alt || ""}" loading="lazy" />
          ${b.alt ? `<figcaption>${b.alt}</figcaption>` : ""}
        </figure>`;
    })
    .join("");
  return `
    <div class="detail-grid">
      <div class="detail-text">${textHTML}</div>
      ${imagesHTML ? `<div class="detail-images">${imagesHTML}</div>` : ""}
    </div>`;
}

function renderProductGrid(containerId, products) {
  const el = document.getElementById(containerId);
  if (!el) return;
  el.innerHTML = products.map(productCardHTML).join("");
  animateGridIn(containerId);
}

function initCarouselProgress(trackId, barId) {
  const track = document.getElementById(trackId);
  const bar = document.getElementById(barId);
  if (!track || !bar) return;
  const update = () => {
    const max = track.scrollWidth - track.clientWidth;
    const ratio = max > 0 ? track.scrollLeft / max : 1;
    bar.style.transform = `scaleX(${Math.min(1, Math.max(0.06, ratio || 0.06))})`;
  };
  track.addEventListener("scroll", update, { passive: true });
  window.addEventListener("resize", update);
  update();
}

function searchResultHTML(product) {
  const wasPrice = product.compareAt
    ? `<span class="text-stone line-through ml-2">${formatAUD(product.compareAt)}</span>`
    : "";
  return `
    <a href="product.html?id=${product.id}" class="flex items-center gap-4 px-4 py-3 hover:bg-off transition-colors">
      <img src="${product.image}" alt="${product.name}" class="w-14 h-14 object-cover flex-shrink-0" loading="lazy" />
      <div class="min-w-0 flex-1">
        <p class="text-[10px] uppercase tracking-wider text-stone font-semibold">${product.brand} &middot; ${product.category}</p>
        <p class="text-sm font-semibold truncate">${product.name}</p>
      </div>
      <p class="text-sm flex-shrink-0">${formatAUD(product.price)}${wasPrice}</p>
    </a>
  `;
}

// Shopify version: the overlay just opens/closes UI chrome now — the form
// inside it submits natively to Shopify's real routes.search_url, replacing
// the static site's live client-side filter against a hardcoded PRODUCTS
// array (which doesn't exist here; there's a real search backend instead).
function initSearch() {
  const overlay = document.getElementById("search-overlay");
  const input = document.getElementById("search-input");
  if (!overlay || !input) return;

  const openSearch = () => {
    overlay.hidden = false;
    document.body.style.overflow = "hidden";
    input.focus();
  };

  const closeSearch = () => {
    overlay.hidden = true;
    document.body.style.overflow = "";
  };

  document.querySelectorAll("[data-search-toggle]").forEach((btn) => {
    btn.addEventListener("click", openSearch);
  });
  document.querySelectorAll("[data-search-close]").forEach((el) => {
    el.addEventListener("click", closeSearch);
  });
  document.addEventListener("keydown", (e) => {
    if (e.key === "Escape" && !overlay.hidden) closeSearch();
  });
}

function prefersReducedMotion() {
  return (
    window.matchMedia &&
    window.matchMedia("(prefers-reduced-motion: reduce)").matches
  );
}

function gsapReady() {
  return typeof gsap !== "undefined";
}

// Homepage hero collage: staggered fade/slide/scale-in on load.
// clearProps removes GSAP's inline transform once the tween finishes so the
// CSS hover-zoom rule (.hero-tile:hover .hero-tile-img) can take back over.
function initHeroAnimation() {
  const tiles = document.querySelectorAll(".hero-tile-img");
  if (!tiles.length) return;
  if (!gsapReady() || prefersReducedMotion()) {
    tiles.forEach((t) => (t.style.opacity = "1"));
    return;
  }
  gsap.set(tiles, { opacity: 0, y: 26, scale: 1.04 });
  gsap.to(tiles, {
    opacity: 1,
    y: 0,
    scale: 1,
    duration: 0.9,
    ease: "power3.out",
    stagger: 0.12,
    clearProps: "transform",
  });
}

// Homepage hero headline: each line staggers up a beat after the collage.
function initHeroHeadline() {
  const lines = document.querySelectorAll("[data-hero-line]");
  if (!lines.length) return;
  if (!gsapReady() || prefersReducedMotion()) return;
  gsap.set(lines, { opacity: 0, y: 24 });
  gsap.to(lines, {
    opacity: 1,
    y: 0,
    duration: 0.9,
    ease: "power3.out",
    stagger: 0.12,
    delay: 0.25,
    clearProps: "transform",
  });
}

// Feature images (About-Exclusive, feature banner) scale down to rest as they
// enter view. The wrapping element needs overflow-hidden so the oversized
// starting scale never pokes outside its box.
function initScaleReveal() {
  if (!gsapReady() || typeof ScrollTrigger === "undefined") return;
  gsap.registerPlugin(ScrollTrigger);
  const els = document.querySelectorAll("[data-scale-reveal]");
  if (!els.length) return;
  if (prefersReducedMotion()) {
    gsap.set(els, { scale: 1 });
    return;
  }
  els.forEach((el) => {
    gsap.fromTo(
      el,
      { scale: 1.12 },
      {
        scale: 1,
        duration: 1.1,
        ease: "power2.out",
        scrollTrigger: { trigger: el, start: "top 85%", once: true },
      }
    );
  });
}

// Stat rows (e.g. About page "1st / AU / 100%") stagger in together instead
// of each number appearing independently.
function initStatsReveal() {
  if (!gsapReady() || typeof ScrollTrigger === "undefined") return;
  gsap.registerPlugin(ScrollTrigger);
  document.querySelectorAll("[data-stat-group]").forEach((group) => {
    const items = group.children;
    if (!items.length) return;
    if (prefersReducedMotion()) {
      gsap.set(items, { opacity: 1, y: 0 });
      return;
    }
    gsap.set(items, { opacity: 0, y: 20 });
    gsap.to(items, {
      opacity: 1,
      y: 0,
      duration: 0.7,
      ease: "power3.out",
      stagger: 0.15,
      scrollTrigger: { trigger: group, start: "top 85%", once: true },
    });
  });
}

// Fades .reveal sections up into place as they scroll into view.
// Falls back to a plain IntersectionObserver (see style.css .reveal/.is-visible)
// if GSAP/ScrollTrigger failed to load.
function initScrollReveal() {
  const els = document.querySelectorAll(".reveal");
  if (!els.length) return;

  if (!gsapReady() || typeof ScrollTrigger === "undefined") {
    if (!("IntersectionObserver" in window)) {
      els.forEach((el) => el.classList.add("is-visible"));
      return;
    }
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add("is-visible");
            observer.unobserve(entry.target);
          }
        });
      },
      { threshold: 0.15, rootMargin: "0px 0px -40px 0px" }
    );
    els.forEach((el) => observer.observe(el));
    return;
  }

  gsap.registerPlugin(ScrollTrigger);

  if (prefersReducedMotion()) {
    gsap.set(els, { opacity: 1, y: 0 });
    return;
  }

  els.forEach((el) => {
    gsap.fromTo(
      el,
      { opacity: 0, y: 28 },
      {
        opacity: 1,
        y: 0,
        duration: 0.8,
        ease: "power3.out",
        scrollTrigger: {
          trigger: el,
          start: "top 88%",
          once: true,
        },
      }
    );
  });
}

// PDP entrance: gallery + buy-box children stagger up on load, a beat
// before any scroll-triggered animation further down the page.
function initPDPEntrance() {
  const els = document.querySelectorAll("[data-pdp-in]");
  if (!els.length) return;
  if (!gsapReady() || prefersReducedMotion()) return;
  gsap.set(els, { opacity: 0, y: 18 });
  gsap.to(els, {
    opacity: 1,
    y: 0,
    duration: 0.7,
    ease: "power3.out",
    stagger: 0.08,
    clearProps: "transform",
  });
}

// Detail-rail images (see renderDetailSections) each animate in with one of
// three distinct styles — slide from either side, or a clip-path wipe —
// rotating per image so a long list of shots doesn't repeat the same beat.
function initDetailImageReveal() {
  const figures = document.querySelectorAll("[data-detail-img]");
  if (!figures.length) return;
  if (!gsapReady() || typeof ScrollTrigger === "undefined") return;
  gsap.registerPlugin(ScrollTrigger);

  if (prefersReducedMotion()) {
    gsap.set(figures, { opacity: 1, x: 0, clipPath: "none" });
    return;
  }

  figures.forEach((fig) => {
    const variant = fig.dataset.anim;
    let fromVars = { opacity: 0 };
    if (variant === "slide-left") fromVars.x = -50;
    else if (variant === "slide-right") fromVars.x = 50;
    else fromVars.clipPath = "inset(0 100% 0 0)";

    gsap.fromTo(fig, fromVars, {
      opacity: 1,
      x: 0,
      clipPath: variant === "clip-reveal" ? "inset(0 0% 0 0)" : undefined,
      duration: 0.9,
      ease: "power3.out",
      clearProps: "transform",
      scrollTrigger: { trigger: fig, start: "top 88%", once: true },
    });
  });
}

// Stagger-in for product grids whenever they're (re)rendered — the shop grid,
// homepage carousel, search results and related-products list all call this
// via renderProductGrid.
function animateGridIn(containerId) {
  if (!gsapReady() || prefersReducedMotion()) return;
  const container = document.getElementById(containerId);
  if (!container) return;
  const cards = container.querySelectorAll(".product-card");
  if (!cards.length) return;
  gsap.set(cards, { opacity: 0, y: 18 });
  gsap.to(cards, {
    opacity: 1,
    y: 0,
    duration: 0.55,
    ease: "power2.out",
    stagger: 0.05,
    clearProps: "transform",
  });
}

// ---------------------------------------------------------------------
// Forge-style homepage hero: smooth scroll, floating shoes, headline mask
// reveal, the z-depth fly-through, and the 01/02/03 narrative panels.
// Same conventions as everything above: guard on gsapReady()/plugin
// presence, guard on prefersReducedMotion(), never leave the page in a
// broken state if the CDN scripts fail to load.
// ---------------------------------------------------------------------

function pluginReady(name) {
  return gsapReady() && typeof window[name] !== "undefined";
}

// Smooth scroll. Must run BEFORE any ScrollTrigger-based init so triggers
// are created against the smoothed scroller rather than the raw window.
function initSmoothScroll() {
  if (!pluginReady("ScrollSmoother") || !pluginReady("ScrollTrigger")) return;
  if (prefersReducedMotion()) return; // native scrolling is the reduced-motion fallback
  if (!document.getElementById("smooth-wrapper")) return;
  gsap.registerPlugin(ScrollTrigger, ScrollSmoother);
  ScrollSmoother.create({
    wrapper: "#smooth-wrapper",
    content: "#smooth-content",
    smooth: 1.15,
    effects: false,
    normalizeScroll: true,
  });
}

// Hero shoes: idle float + mouse parallax + scroll parallax, layered.
// Float owns `y`/`rotation`; parallax owns `xPercent`/`yPercent`. Keeping
// them on separate properties is what stops the three tweens fighting.
function initHeroShoes() {
  // Query with plain DOM first — gsap.utils.toArray touches `gsap` itself,
  // which throws if the core library failed to load at all (not just a
  // plugin), before we ever reach the gsapReady() guard below.
  const shoeEls = document.querySelectorAll(".hero-shoe");
  if (!shoeEls.length) return;
  if (!gsapReady() || prefersReducedMotion()) {
    if (gsapReady()) gsap.set(shoeEls, { opacity: 1 });
    return;
  }
  const shoes = Array.from(shoeEls);

  shoes.forEach((el) => {
    const depth = parseFloat(el.dataset.depth) || 0.1;

    gsap.to(el, {
      y: "+=" + (8 + depth * 28),
      rotation: 1.2 + depth * 2,
      duration: 4 + Math.random() * 3,
      delay: Math.random() * 1.5,
      ease: "sine.inOut",
      yoyo: true,
      repeat: -1,
    });

    if (pluginReady("ScrollTrigger")) {
      gsap.to(el, {
        yPercent: depth * -120,
        ease: "none",
        scrollTrigger: {
          trigger: "#hero",
          start: "top top",
          end: "bottom top",
          scrub: true,
        },
      });
    }
  });

  // Pointer parallax — desktop pointers only. quickTo avoids allocating a
  // new tween on every single pointermove event.
  if (!window.matchMedia("(hover: hover) and (pointer: fine)").matches) return;
  const setters = shoes.map((el) => ({
    depth: parseFloat(el.dataset.depth) || 0.1,
    x: gsap.quickTo(el, "xPercent", { duration: 0.9, ease: "power2.out" }),
  }));
  window.addEventListener(
    "pointermove",
    (e) => {
      const nx = (e.clientX / window.innerWidth - 0.5) * 2; // -1 .. 1
      setters.forEach((s) => s.x(nx * s.depth * 34));
    },
    { passive: true }
  );
}

// Hero headline: per-line mask reveal.
function initHeroTitle() {
  const title = document.querySelector(".hero-title");
  if (!title) return;
  if (!pluginReady("SplitText") || prefersReducedMotion()) return;
  gsap.registerPlugin(SplitText);
  // mask: "lines" auto-wraps each line in its own overflow-hidden element,
  // so the translated .split-line itself never needs overflow:hidden —
  // clipping a translating element by its own overflow doesn't work.
  const split = new SplitText(title, { type: "lines", mask: "lines", linesClass: "split-line" });
  gsap.set(split.lines, { yPercent: 110 });
  gsap.to(split.lines, {
    yPercent: 0,
    duration: 1.15,
    ease: "power3.out",
    stagger: 0.12,
    delay: 0.15,
  });
}

// Z-depth fly-through. Layers travel toward the camera on a scrubbed timeline.
// Pins .fly-stage with ScrollTrigger's own pin:true rather than CSS
// position:sticky — sticky never engages under ScrollSmoother, since
// ScrollSmoother fakes scrolling with a transform instead of real native
// scroll and sticky has nothing to react to. pin:true is what ScrollTrigger
// and ScrollSmoother are built to coordinate directly.
function initFlythrough() {
  // Plain DOM query first — see the comment in initHeroShoes on why
  // gsap.utils.toArray can't run before the readiness guard below.
  const stage = document.querySelector(".fly-stage");
  const layerEls = document.querySelectorAll(".fly-layer");
  if (!stage || !layerEls.length) return;
  if (!pluginReady("ScrollTrigger") || prefersReducedMotion()) {
    if (gsapReady()) {
      gsap.set(layerEls, { opacity: 1, scale: 1, filter: "none", position: "static" });
    }
    return;
  }
  gsap.registerPlugin(ScrollTrigger);
  const layers = Array.from(layerEls);

  const tl = gsap.timeline({
    scrollTrigger: {
      trigger: stage,
      start: "top top",
      end: "+=" + window.innerHeight * 2,
      scrub: 0.8,
      pin: true,
    },
  });

  layers.forEach((layer, i) => {
    gsap.set(layer, { scale: 0.32, opacity: 0, filter: "blur(9px)" });
    tl.to(
      layer,
      {
        keyframes: [
          { scale: 1, opacity: 1, filter: "blur(0px)", duration: 0.5 },
          { scale: 3.4, opacity: 0, filter: "blur(7px)", duration: 0.5 },
        ],
        ease: "none",
      },
      i * 0.42
    );
  });
}

// 01/02/03 panels: figure clip-reveal + background scale scrub.
function initPanels() {
  // Plain DOM query first — see the comment in initHeroShoes on why
  // gsap.utils.toArray can't run before the readiness guard below.
  const panels = document.querySelectorAll(".panel");
  if (!panels.length) return;
  if (!pluginReady("ScrollTrigger") || prefersReducedMotion()) {
    if (gsapReady()) gsap.set(".panel-figure", { clipPath: "none", opacity: 1 });
    return;
  }
  gsap.registerPlugin(ScrollTrigger);

  panels.forEach((panel) => {
    const fig = panel.querySelector(".panel-figure");
    const bg = panel.querySelector(".panel-bg");

    if (fig) {
      gsap.fromTo(
        fig,
        { clipPath: "inset(0 0 100% 0)" },
        {
          clipPath: "inset(0 0 0% 0)",
          duration: 1.2,
          ease: "power3.out",
          scrollTrigger: { trigger: panel, start: "top 72%", once: true },
        }
      );
    }
    if (bg) {
      gsap.fromTo(
        bg,
        { scale: 1.15 },
        {
          scale: 1.28,
          ease: "none",
          scrollTrigger: { trigger: panel, start: "top bottom", end: "bottom top", scrub: true },
        }
      );
    }
  });
}

function initScrollRail() {
  const fill = document.querySelector(".scroll-rail-fill");
  if (!fill) return;
  if (!pluginReady("ScrollTrigger")) return;
  gsap.registerPlugin(ScrollTrigger);
  gsap.to(fill, {
    scaleX: 1,
    ease: "none",
    scrollTrigger: { start: 0, end: "max", scrub: 0.3 },
  });
}

document.addEventListener("DOMContentLoaded", () => {
  // Each init runs independently so one failure can't silently break the rest
  // (e.g. leaving nav/search/cart unresponsive until a hard refresh).
  const safeInit = (fn) => {
    try {
      fn();
    } catch (err) {
      console.error(err);
    }
  };
  safeInit(initMobileNav);
  safeInit(initSearch);
  // initHeroAnimation / initHeroHeadline targeted the old hero-collage
  // markup, which the Forge-style hero (initHeroShoes/initHeroTitle) below
  // replaced. Left defined further up the file — they no-op safely if
  // called, but nothing on the current pages calls them anymore.
  safeInit(initSmoothScroll); // must run before any ScrollTrigger-based init
  safeInit(initHeroShoes);
  safeInit(initHeroTitle);
  safeInit(initFlythrough);
  safeInit(initPanels);
  safeInit(initScrollRail);
  safeInit(initScaleReveal);
  safeInit(initStatsReveal);
  safeInit(initScrollReveal);
  safeInit(initPDPEntrance);
  safeInit(initDetailImageReveal);
});
